/**
 * Trustee Overview — a simplified Executive Overview tailored for trustees.
 *
 * Reuses the same data hook (`useCommandCentreData`) and the India Map / KPI
 * side panel from CommandCentre. All financials are LIVE-computed (with the
 * ₹936.56 Cr Total Sanctioned uplift applied per the standard rule),
 * consistent with Executive Overview. Differences vs. Executive Overview:
 *   • No "Welcome back" greeting
 *   • Side panel ends after the Geographic card (no large Aspirational tiles)
 *   • "Sanctioned by Theme" stacked bar retained below the map
 *   • Grants table retained at the bottom (the GRANTS "View →" link scrolls to it)
 *   • View mode is locked to "All Grants" (New + Ongoing) via Layout-level filter chips
 *
 * Reuses the same data hook (`useCommandCentreData`) and the India Map / KPI
 * side panel from CommandCentre. Differences vs. Executive Overview:
 *   • No "Welcome back" greeting
 *   • Side panel ends after the Geographic card (no large Aspirational tiles)
 *   • "Sanctioned by Theme" stacked bar retained below the map
 *   • Grants table retained at the bottom (the GRANTS "View →" link scrolls to it)
 *   • View mode is locked to "All Grants" (New + Ongoing) via Layout-level filter chips
 */
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  FileText, Info, Download, Search, ChevronLeft, ChevronRight, ChevronUp,
  ChevronDown, ChevronsUpDown, X, MapPin, Map, Grid3X3, Target, Crosshair,
  IndianRupee, ArrowRightLeft, Wallet, Clock,
  Users,
} from "lucide-react";
import IndiaMap from "../components/IndiaMap";
import ThemeChip from "../components/ThemeChip";
import { fmtCr, fmtNum, displayTheme } from "../utils/format";
import { useCommandCentreData } from "../hooks/useCommandCentreData";
import { useFilters } from "../context/FilterContext";
import { applyUplift } from "../utils/sanctionedUplift";

const PIE_COLORS = [
  "#B71C1C", "#16A34A", "#F59E0B", "#475569", "#8B5CF6",
  "#0EA5E9", "#EA580C", "#0D9488", "#DB2777", "#4F46E5",
];

export default function TrusteeOverview() {
  const navigate = useNavigate();
  const {
    summary, stateData, allocationByTheme,
    loading, sf, s, hasGrants, g, totalSanctioned,
    selectedState, selectedDistrict, districtData, blockData, mapLoading,
    handleStateSelect, handleDistrictSelect,
    aspirationalData,
    mapViewMode, setMapViewMode, allDistrictData,
    grants, grantsLoading, grantsPage, setGrantsPage, grantsTotal,
    grantsTotalPages, grantsSearchInput, setGrantsSearchInput,
    grantsSortBy, grantsSortOrder, grantsExporting,
    handleGrantsSort, handleGrantsSearch, clearGrantsSearch,
    handleGrantsExport, scrollToGrantsTable, grantsTableRef,
    grantsTableOpen, setGrantsTableOpen,
    filters,
  } = useCommandCentreData();

  // FY-adaptive Trustee financial block — ALL Sanctioned/Disbursed live-computed.
  // Chained opening-balance rule: closing of year N = opening of year N+1.
  //   FY 24-25 → 3 cards (no OB, unknown baseline)         Closing = Sanc − Disb
  //   FY 25-26 → 4 cards, OB = FY 24-25 closing            Closing = OB + Sanc − Disb
  //   FY 26-27 → 4 cards, OB = FY 25-26 closing            Closing = OB + Sanc − Disb
  const singleFy = (Array.isArray(filters.fy) && filters.fy.length === 1) ? filters.fy[0] : null;
  const isDefaultBase = !filters.theme && !selectedState;
  const isDefaultFy2425 = isDefaultBase && singleFy === "FY 2024-25";
  const isDefaultFy2526 = isDefaultBase && singleFy === "FY 2025-26";
  const isDefaultFy2627 = isDefaultBase && singleFy === "FY 2026-27";
  const showTrusteeCards = isDefaultFy2425 || isDefaultFy2526 || isDefaultFy2627;

  // Fetch prior-year summaries so we can chain the opening balance.
  const { API, viewMode, setFilters } = useFilters();

  // Auto-clear theme filter on mount — theme filter is not exposed on Trustee page
  // (per stakeholder direction Sep 10, 2026) so any stale value from other pages
  // must be reset to avoid silently narrowing the trustee financials.
  useEffect(() => {
    setFilters((f) => (f.theme ? { ...f, theme: "" } : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Trustee-specific card semantics (Feb 2026 redefinition) ──────────────
  //   Sanctioned tile   = sum of `total_allocation_cr` for grants that were
  //                       APPROVED in the selected FY (backend `view_mode=snapshot`).
  //                       For "All Years"/multi-FY selections we sum snapshots
  //                       across FY 24-25, 25-26 and 26-27.
  //   Disbursement tile = payments made in the selected FY window for the
  //                       "New + Ongoing" grant set (backend `view_mode=active`
  //                       YTD figure, i.e. `total_disbursement_cr`).
  //   Uplift is NEVER added here — "grants approved in FY" is the snapshot
  //   semantic which the uplift rule already suppresses.
  const fyArr = Array.isArray(filters.fy) ? filters.fy : (filters.fy ? [filters.fy] : []);
  const isAllYears = fyArr.length === 0;
  const isMultiFy = fyArr.length > 1;
  const trusteeFyLabel = isAllYears ? "All Years" : (fyArr.length === 1 ? fyArr[0] : fyArr.join(", "));
  const sanctionedLabel = `Sanctioned (Grants approved in ${trusteeFyLabel})`;

  // Fetch per-FY snapshot sanctioned so the trustee tile can display
  // "grants approved in FY <X>" for whichever FYs are selected. Cached in
  // a map keyed by FY string. When All Years is selected, we fetch all 3.
  const [snapshotByFy, setSnapshotByFy] = useState({});
  useEffect(() => {
    const needFys = isAllYears
      ? ["FY 2024-25", "FY 2025-26", "FY 2026-27"]
      : fyArr;
    if (!needFys.length) return;
    Promise.all(needFys.map(fy =>
      axios.get(`${API}/executive/summary`, { params: { fy, view_mode: "snapshot" } })
        .then(r => [fy, r.data?.total_allocation_cr || 0])
        .catch(() => [fy, 0])
    )).then(pairs => {
      const next = {};
      for (const [fy, v] of pairs) next[fy] = v;
      setSnapshotByFy(next);
    });
    // Intentionally re-fetch on any filter change since the snapshot value
    // is filter-independent for FY only — theme/state/etc. still refine via
    // params below on the LIVE fallback, not on this per-FY roll-up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, JSON.stringify(fyArr), isAllYears]);

  const snapshotSanctionedForFy = (fy) => snapshotByFy[fy] || 0;
  const snapshotSanctionedTotal = Object.values(snapshotByFy).reduce((a, b) => a + b, 0);

  // ── Strategic Initiatives segment — shown in the "Sanctioned
  // by Theme" chart only when FY 2025-26 is the sole selected FY AND we're
  // viewing All India (no state/district drilled into).
  const isOnlyFy2526 = Array.isArray(filters.fy) && filters.fy.length === 1 && filters.fy[0] === "FY 2025-26";
  const isAllIndia = !selectedState && !selectedDistrict;
  const applyFy2526Adjustments = isOnlyFy2526 && isAllIndia;
  const STRATEGIC_INITIATIVES_CR = 1051.53;
  const themeChartTotal = applyFy2526Adjustments ? totalSanctioned + STRATEGIC_INITIATIVES_CR : totalSanctioned;

  const statusClass = (s) =>
    s === "Active" || s === "Ongoing" || s === "Under Closure" ? "bg-green-100 text-green-700" :
    s === "Closed" ? "bg-slate-100 text-slate-500" :
    "bg-amber-100 text-amber-700";

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "–";

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B71C1C]" />
      </div>
    );
  }

  return (
    <div data-testid="trustee-overview" className="space-y-3">
      {/* Row 1: Map + Side Panel (bottoms aligned via items-stretch) */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-3 items-stretch">

        {/* LEFT: Map */}
        <div className="min-w-0 overflow-hidden">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-full flex items-center justify-center">
            <div className="p-3 overflow-hidden flex justify-center w-full">
              {sf ? (
                <IndiaMap
                  stateData={stateData}
                  districtData={districtData}
                  blockData={blockData}
                  allDistrictData={allDistrictData}
                  onStateSelect={handleStateSelect}
                  onDistrictSelect={handleDistrictSelect}
                  selectedState={selectedState}
                  selectedDistrict={selectedDistrict}
                  dataLoading={mapLoading}
                  mapViewMode={mapViewMode}
                  onMapViewChange={(mode) => { setMapViewMode(mode); if (selectedState) handleStateSelect(null); }}
                />
              ) : (
                <div className="h-64 flex items-center justify-center bg-slate-50 rounded-lg text-slate-600">
                  Map requires data connection
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Metrics Panel (Grants / Financial / Geographic) */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-3 flex flex-col gap-2">

            {/* Grants Summary */}
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Grants</h3>
                <button onClick={scrollToGrantsTable} className="text-[10px] text-[#B71C1C] hover:underline" data-testid="grants-view-scroll">View →</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div
                  onClick={scrollToGrantsTable}
                  className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-100 cursor-pointer hover:border-[#B71C1C]/30 transition-colors"
                  data-testid="kpi-grants-approved"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#B71C1C] flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold text-slate-900 leading-none">{fmtNum(s?.total_grants || s?.grants)}</span>
                    <div className="text-[10px] text-slate-500 mt-0.5">All Grants</div>
                  </div>
                </div>
                <div
                  onClick={scrollToGrantsTable}
                  className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-100 cursor-pointer hover:border-green-300 transition-colors"
                  data-testid="kpi-grantees"
                >
                  <div className="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center flex-shrink-0">
                    <Users size={18} className="text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold text-slate-900 leading-none">{fmtNum(s?.active_grantees || s?.grantees)}</span>
                    <div className="text-[10px] text-slate-500 mt-0.5">Grantees</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary — FY-adaptive layout with new trustee semantics.
                Sanctioned tile = grants approved in the selected FY (snapshot).
                Disbursement tile = payments made in the FY for New+Ongoing (YTD).
                • FY 24-25 → 3 cards (no OB) — live values.
                • FY 25-26 → 4 cards, ALL STATIC (Finance-approved actuals).
                • FY 26-27 → 4 cards; OB = FY 25-26 static closing (₹2,622 Cr),
                             Sanctioned = live snapshot, Disbursement = live YTD.
                Other combos (multi-FY, All Years, theme/state filtered) → LIVE fallback below. */}
            {showTrusteeCards ? (
              (() => {
                // ⚠️ DO NOT MODIFY — Finance-approved static actuals for FY 25-26 (locked Feb 2026).
                //    These flow into: FY 25-26 (all 4 cards) AND FY 26-27 OB (chained from closing=2622).
                //    Any change here must be requested by Finance + explicit user greenlight.
                const STATIC_FY2526 = Object.freeze({ ob: 1621, sanctioned: 2384, disbursement: 1383, closing: 2622 });
                // ⚠️ DO NOT MODIFY — Finance-approved static OPENING BALANCE for FY 26-27
                //    (locked Feb 9, 2026). Only the OB is hardcoded; Sanctioned & Disbursement
                //    are live-computed, and Closing is derived (OB + Sanctioned − Disbursement).
                const STATIC_FY2627 = Object.freeze({ ob: 2621.71, sanctionedYtd: 528.74 });  // Ch10 (Sep 10, 2026): Sanctioned YTD Finance-locked.

                const openingBal = isDefaultFy2627 ? STATIC_FY2627.ob
                                 : isDefaultFy2526 ? STATIC_FY2526.ob
                                 : null;
                const sanctionedFy = isDefaultFy2526 ? STATIC_FY2526.sanctioned
                                   : isDefaultFy2627 ? STATIC_FY2627.sanctionedYtd
                                   : snapshotSanctionedForFy(fyArr[0] || "");
                // Disbursement = payments in the FY for New+Ongoing = YTD ("total_disbursement_cr").
                const disbursementFy = isDefaultFy2526 ? STATIC_FY2526.disbursement
                                     : (s?.total_disbursement_cr || s?.disbursement_cr || 0);
                const closingBal = isDefaultFy2526 ? STATIC_FY2526.closing
                                 : ((openingBal || 0) + sanctionedFy - disbursementFy);
                const eoyLabel = isDefaultFy2627 ? "Balance commitment"
                               : isDefaultFy2526 ? "Balance commitment as on 31.03.2026"
                               : "Balance commitment as on 31.03.2025";
                const obChainText = (() => {
                  if (isDefaultFy2526) return `= Opening Balance for FY 25-26 (${fmtCr(STATIC_FY2526.ob)})`;
                  if (isDefaultFy2627) return `= FY 25-26 Closing Balance (${fmtCr(STATIC_FY2526.closing)}) reconciled by Finance to ${fmtCr(STATIC_FY2627.ob)}`;
                  return "";
                })();
                return (
                  <div className="bg-slate-50 rounded-lg p-2.5" data-testid={isDefaultFy2627 ? "financial-card-fy2627" : isDefaultFy2526 ? "financial-card-fy2526" : "financial-card-fy2425"}>
                    <div className="flex items-center justify-between mb-1.5">
                      <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Financial</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {(isDefaultFy2526 || isDefaultFy2627) && (
                        <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-2.5 border border-blue-100 flex flex-col justify-start min-h-[88px] relative group cursor-help" data-testid="kpi-opening-balance">
                          <div className="flex items-start gap-1.5 mb-1.5">
                            <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-100 flex-shrink-0"><IndianRupee size={11} className="text-blue-600" /></div>
                            <span className="text-[10px] text-blue-700 font-medium leading-tight">Balance Commitment / Opening Balance</span>
                            <Info size={10} className="text-blue-400 ml-auto flex-shrink-0" />
                          </div>
                          <span className="text-lg font-bold text-slate-900">{fmtCr(openingBal)}</span>
                          {isDefaultFy2627 && <span className="text-[9px] text-slate-500 mt-0.5">carried over from FY 25-26 closing</span>}
                          {obChainText && (
                            <div className="absolute left-0 top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-40 max-w-xs whitespace-pre-line leading-relaxed pointer-events-none" data-testid="ob-chain-tooltip">
                              <span className="font-semibold text-[11px] block mb-1">Opening Balance derivation</span>
                              {obChainText}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-2.5 border border-emerald-100 flex flex-col justify-start min-h-[88px]" data-testid="kpi-sanctioned-live">
                        <div className="flex items-start gap-1.5 mb-1.5">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-emerald-100 flex-shrink-0"><ArrowRightLeft size={11} className="text-emerald-600" /></div>
                          <span className="text-[10px] text-emerald-700 font-medium leading-tight">{sanctionedLabel}</span>
                        </div>
                        <span className="text-lg font-bold text-slate-900">{fmtCr(sanctionedFy)}</span>
                        {isDefaultFy2627 && (
                          <span className="text-[9px] text-slate-500 mt-0.5 leading-tight" data-testid="kpi-sanctioned-ytd-subtext">
                            Additional circular worth 301.98 Cr sanctioned &amp; under grant letter process
                          </span>
                        )}
                      </div>
                      <div className="bg-gradient-to-br from-violet-50 to-white rounded-lg p-2.5 border border-violet-100 flex flex-col justify-start min-h-[88px]" data-testid="kpi-disbursed-live">
                        <div className="flex items-start gap-1.5 mb-1.5">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-violet-100 flex-shrink-0"><Wallet size={11} className="text-violet-600" /></div>
                          <span className="text-[10px] text-violet-700 font-medium leading-tight">Disbursement (New + Ongoing)</span>
                        </div>
                        <span className="text-lg font-bold text-slate-900">{fmtCr(disbursementFy)}</span>
                        {isDefaultFy2627 && (
                          <span className="text-[9px] text-slate-500 mt-0.5 leading-tight" data-testid="kpi-disbursed-cumulative-subtext">
                            205 Cr under process; 53 Cr under Individual grants disbursed
                          </span>
                        )}
                      </div>
                      <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg p-2.5 border border-amber-100 flex flex-col justify-start min-h-[88px]" data-testid="kpi-fc-eoy-live">
                        <div className="flex items-start gap-1.5 mb-1.5">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-amber-100 flex-shrink-0"><Clock size={11} className="text-amber-600" /></div>
                          <span className="text-[10px] text-amber-700 font-medium leading-tight">{eoyLabel}</span>
                        </div>
                        <span className="text-lg font-bold text-slate-900">{fmtCr(closingBal)}</span>
                        <span className="text-[9px] text-slate-500 mt-0.5">
                          {(isDefaultFy2526 || isDefaultFy2627) ? "= OB + Sanctioned − Disbursement" : "= Sanctioned − Disbursement"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
            /* LIVE fallback — theme/state filter, multi-FY, or All Years.
               • All Years / multi-FY (no theme/state) → mirror Executive
                 Overview: Sanctioned (New + Ongoing) with the ₹936.56 Cr
                 uplift, Disbursement dual-line (Till Date + YTD). This is the
                 explicit trustee-request: All Years must match Executive.
               • Theme-/state-filtered slices → keep the snapshot roll-up. */
            (() => {
              const mirrorExecutive = isAllYears || isMultiFy;
              const execSanctioned = applyUplift(s?.total_allocation_cr, filters, viewMode, selectedState, selectedDistrict);
              const execTillDate = s?.cumulative_disbursement_cr || 0;
              const execYtd = s?.total_disbursement_cr || s?.disbursement_cr || 0;

              return (
                <div className="bg-slate-50 rounded-lg p-2.5" data-testid="financial-card-live">
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Financial</h3>
                    {s?.is_apportioned && (
                      <div className="group relative">
                        <Info size={13} className="text-amber-500 cursor-help" data-testid="apportionment-info-icon" />
                        <div className="absolute right-0 top-5 z-50 hidden group-hover:block w-64 p-2.5 bg-slate-800 text-white text-[10px] leading-relaxed rounded-lg shadow-lg">
                          Amounts are proportionally estimated. Multi-state grants are split by block/district count in this region.
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-2 border border-blue-100 flex flex-col justify-start min-h-[68px]" data-testid="kpi-sanctioned">
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-100"><IndianRupee size={11} className="text-blue-600" /></div>
                        <span className="text-[10px] text-blue-700 font-medium leading-tight">{mirrorExecutive ? "Sanctioned (New + Ongoing)" : sanctionedLabel}</span>
                        {s?.is_apportioned && <span className="text-[8px] text-amber-500 font-medium">~est.</span>}
                      </div>
                      <span className="text-lg font-bold text-slate-900">{fmtCr(mirrorExecutive ? execSanctioned : snapshotSanctionedTotal)}</span>
                    </div>
                    {mirrorExecutive ? (
                      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-2 border border-emerald-100 flex flex-col justify-start min-h-[68px]" data-testid="kpi-cumulative-disbursement">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-emerald-100"><ArrowRightLeft size={11} className="text-emerald-600" /></div>
                          <span className="text-[10px] text-emerald-700 font-medium leading-tight">Disbursement <span className="text-emerald-500 font-normal">(till date, cumulative)</span></span>
                        </div>
                        <span className="text-lg font-bold text-slate-900" data-testid="kpi-disbursement-till-date">{fmtCr(execTillDate)}</span>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-2 border border-emerald-100 flex flex-col justify-start min-h-[68px]" data-testid="kpi-cumulative-disbursement">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-emerald-100"><ArrowRightLeft size={11} className="text-emerald-600" /></div>
                          <span className="text-[10px] text-emerald-700 font-medium">Disbursement (New + Ongoing)</span>
                        </div>
                        <span className="text-lg font-bold text-slate-900">{fmtCr(execYtd)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()
            )}

            {/* Geographic Stats */}
            <div className="bg-slate-50 rounded-lg p-2.5" data-testid="geographic-card">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Geographic</h3>
                <button onClick={() => navigate('/geographic')} className="text-[10px] text-[#B71C1C] hover:underline">Map →</button>
              </div>
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <MapPin size={14} className="text-blue-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.states) || '–'}</div>
                      <div className="text-[10px] text-slate-500">States & UT</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <Map size={14} className="text-teal-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.districts) || '–'}</div>
                      <div className="text-[10px] text-slate-500">Districts</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Grid3X3 size={14} className="text-slate-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.blocks) || '–'}</div>
                      <div className="text-[10px] text-slate-500">Blocks</div>
                    </div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 border border-amber-200 flex items-center gap-2" data-testid="asp-districts-card">
                    <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Target size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-amber-700 leading-tight">{aspirationalData.ad?.covered || 0}/{aspirationalData.ad?.total || 0}</div>
                      <div className="text-[10px] text-slate-500">Asp. Districts</div>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2 border border-purple-200 flex items-center gap-2 justify-center" data-testid="asp-blocks-card">
                  <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Crosshair size={14} className="text-purple-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-purple-700 leading-tight">{aspirationalData.ab?.covered || 0}/{aspirationalData.ab?.total || 0}</div>
                    <div className="text-[10px] text-slate-500">Aspirational Blocks</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Row 2: (Sanctioned by Theme chart removed per stakeholder request, Feb 2026) */}

      {/* ── Grants Table (retained — GRANTS "View →" scrolls here) ──── */}
      <div ref={grantsTableRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="trustee-grants-table-section">
        <div
          className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between cursor-pointer select-none"
          onClick={() => setGrantsTableOpen(!grantsTableOpen)}
          data-testid="trustee-grants-table-toggle"
        >
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              Grants
              <ChevronDown size={14} className={`text-slate-600 transition-transform ${grantsTableOpen ? "rotate-180" : ""}`} />
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <p className="text-xs text-slate-500">{grantsTotal.toLocaleString()} grants</p>
              {grantsTableOpen && <span className="text-[11px] text-[#B71C1C] font-semibold">Click on Grant ID to view more details</span>}
              {selectedState && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-[10px] font-medium" data-testid="grants-state-filter-tag">
                  {selectedState}{selectedDistrict ? ` / ${selectedDistrict}` : ""}
                </span>
              )}
            </div>
          </div>
          {grantsTableOpen && (
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <form onSubmit={handleGrantsSearch} className="flex items-center gap-1.5">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600" />
                  <input
                    type="text"
                    value={grantsSearchInput}
                    onChange={(e) => setGrantsSearchInput(e.target.value)}
                    placeholder="Search grants..."
                    className="pl-8 pr-7 py-1.5 border border-slate-200 rounded-lg text-xs w-56 focus:outline-none focus:ring-2 focus:ring-[#B71C1C]/20 focus:border-[#B71C1C]"
                    data-testid="trustee-grants-search"
                  />
                  {grantsSearchInput && (
                    <button type="button" onClick={clearGrantsSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-600">
                      <X size={12} />
                    </button>
                  )}
                </div>
                <button type="submit" className="px-2.5 py-1.5 bg-[#B71C1C] text-white rounded-lg text-xs font-medium hover:bg-[#8B1515]" data-testid="trustee-grants-search-btn">Search</button>
              </form>
              <button
                onClick={handleGrantsExport}
                disabled={grantsExporting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-60 transition-colors whitespace-nowrap"
                data-testid="trustee-grants-export"
              >
                <Download size={13} />
                {grantsExporting ? "Exporting..." : "Export CSV"}
              </button>
            </div>
          )}
        </div>

        {grantsTableOpen && (
          <>
            {grantsLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B71C1C]" />
              </div>
            ) : grants.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-600">
                <FileText size={40} className="mb-2 opacity-40" />
                <p className="text-sm">No grants found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="trustee-grants-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <SortTh label="Grant ID"     field="grant_id"           sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="px-3" />
                      <SortTh label="Project Title" field="project_title"     sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Grantee"      field="grantee_name"       sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Portfolio"    field="theme"              sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Status"       field="status"             sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Sanctioned"   field="amount_recommended" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right" />
                      <SortTh label="Disbursed (Till Date)" field="disbursed"     sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right"
                        tooltip="Till Date = sum of all payments for this grant up to today's date, regardless of FY." />
                      <SortTh label="Disbursed (YTD)"       field="disbursed_ytd" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right"
                        tooltip="YTD = payments made only in the FY selected in the top filter." />
                      {selectedState && (
                        <>
                          <th className="text-left font-medium text-amber-600 uppercase py-2 px-2 whitespace-nowrap text-[10px] bg-amber-50/50">Sanctioned ({selectedDistrict || selectedState})</th>
                          <th className="text-left font-medium text-amber-600 uppercase py-2 px-2 whitespace-nowrap text-[10px] bg-amber-50/50">Disbursed ({selectedDistrict || selectedState})</th>
                        </>
                      )}
                      <SortTh label="Start Date"   field="project_start_date" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="End Date"     field="project_end_date"   sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="FY"           field="fy"                 sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Grant Approved Date" field="grant_approved_date" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((g, i) => (
                      <tr key={g.grant_id || i} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}>
                        <td className="py-2 px-3 font-mono whitespace-nowrap">
                          <button onClick={() => navigate(`/grant-overview?grant=${g.grant_id}`)}
                            className="text-[#B71C1C] hover:underline cursor-pointer font-medium">{g.grant_id || "–"}</button>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className="font-medium text-slate-800 max-w-[200px] truncate block" title={g.project_title}>{g.project_title || "–"}</span>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className="text-slate-700 max-w-[140px] truncate block" title={g.grantee}>{g.grantee || "–"}</span>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">{g.theme ? <ThemeChip theme={g.theme} /> : "–"}</td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${statusClass(g.status)}`}>{g.status || "–"}</span>
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-slate-800 whitespace-nowrap">{g.amount_cr ? fmtCr(g.amount_cr) : "–"}</td>
                        <td className="py-2 px-2 text-right font-medium text-green-700 whitespace-nowrap">{g.disbursed_cr ? fmtCr(g.disbursed_cr) : "–"}</td>
                        <td className="py-2 px-2 text-right font-medium text-emerald-800 whitespace-nowrap">{g.disbursed_ytd_cr ? fmtCr(g.disbursed_ytd_cr) : "–"}</td>
                        {selectedState && (
                          <>
                            <td className="py-2 px-2 text-right font-medium text-amber-700 whitespace-nowrap bg-amber-50/30">{g.sanctioned_geo_cr != null ? fmtCr(g.sanctioned_geo_cr) : "–"}</td>
                            <td className="py-2 px-2 text-right font-medium text-amber-700 whitespace-nowrap bg-amber-50/30">{g.disbursed_geo_cr != null ? fmtCr(g.disbursed_geo_cr) : "–"}</td>
                          </>
                        )}
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.start_date)}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.end_date)}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{g.fy || "–"}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.approved_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Pagination */}
        {grantsTableOpen && grantsTotalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing {((grantsPage - 1) * 50) + 1}–{Math.min(grantsPage * 50, grantsTotal)} of {grantsTotal} grants
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setGrantsPage(p => Math.max(1, p - 1))} disabled={grantsPage === 1}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100" data-testid="trustee-grants-prev">
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-slate-600">Page {grantsPage} of {grantsTotalPages}</span>
              <button onClick={() => setGrantsPage(p => Math.min(grantsTotalPages, p + 1))} disabled={grantsPage === grantsTotalPages}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100" data-testid="trustee-grants-next">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── SortTh Sub-Component ────────────────────────────────────────── */
function SortTh({ label, field, sortBy, sortOrder, onSort, className = "", tooltip = "" }) {
  const active = sortBy === field;
  return (
    <th
      className={`text-left font-medium text-slate-500 uppercase py-2 px-2 whitespace-nowrap cursor-pointer select-none hover:text-slate-800 hover:bg-slate-100 transition-colors group ${className}`}
      onClick={() => onSort(field)}
      data-testid={`trustee-sort-${field}`}
      title={tooltip || undefined}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {tooltip && <span className="text-slate-400 text-[10px]" data-testid={`th-tooltip-${field}`}>ⓘ</span>}
        <span className={`transition-colors ${active ? "text-[#B71C1C]" : "text-slate-500 group-hover:text-slate-600"}`}>
          {active
            ? sortOrder === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
            : <ChevronsUpDown size={12} />}
        </span>
      </span>
    </th>
  );
}

/* ─── ThemePortfolioBars: single horizontal stacked bar (segments per theme + optional Strategic Initiatives) ── */
const STRATEGIC_INITIATIVES_COLOR = "#7F1D1D";
function ThemePortfolioBars({ data, sf, extraSegment = null }) {
  const valueKey = sf ? "allocation_cr" : "disbursed_cr";
  const sorted = [...data].sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0));
  const segments = sorted.map((d, i) => ({
    theme: d.theme,
    value: Number(d[valueKey] || 0),
    color: PIE_COLORS[i % PIE_COLORS.length],
    isExtra: false,
  }));
  if (extraSegment && extraSegment.value > 0) {
    segments.push({
      theme: extraSegment.theme,
      value: Number(extraSegment.value),
      color: STRATEGIC_INITIATIVES_COLOR,
      isExtra: true,
    });
  }
  const total = segments.reduce((s, d) => s + (d.value || 0), 0);
  segments.forEach((seg) => {
    seg.pct = total > 0 ? +((seg.value / total) * 100).toFixed(1) : 0;
  });

  return (
    <div className="flex flex-col gap-4" data-testid="theme-portfolio-bars">
      <div className="h-8 w-full rounded-lg overflow-hidden flex shadow-inner bg-slate-100" data-testid="theme-stacked-bar">
        {segments.map((d) => {
          const w = total > 0 ? ((d.value || 0) / total) * 100 : 0;
          if (w <= 0) return null;
          return (
            <div
              key={d.theme}
              className="h-full relative group transition-all duration-300 hover:brightness-110"
              style={{ width: `${w}%`, backgroundColor: d.color }}
              data-testid={`theme-segment-${d.theme}`}
              title={`${d.isExtra ? d.theme : displayTheme(d.theme)}: ${d.pct}% · ₹${d.value.toFixed(1)} Cr`}
            >
              {w >= 6 && (
                <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-white drop-shadow"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
                  {d.pct}%
                </span>
              )}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-2.5 py-1.5 shadow-lg z-30 whitespace-nowrap pointer-events-none">
                <span className="font-bold block">{d.isExtra ? d.theme : displayTheme(d.theme)}</span>
                <span className="block opacity-80">₹{d.value.toFixed(1)} Cr · {d.pct}%</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-5 gap-x-3 gap-y-1.5">
        {segments.map((d) => (
          <div
            key={d.theme}
            className="flex items-center gap-1.5 text-[11px] min-w-0"
            title={`${d.isExtra ? d.theme : displayTheme(d.theme)} · ₹${d.value.toFixed(1)} Cr · ${d.pct}%`}
            data-testid={`theme-legend-${d.theme}`}
          >
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: d.color }} />
            <span className={`font-medium truncate flex-1 ${d.isExtra ? "text-[#7F1D1D] font-semibold" : "text-slate-700"}`}>
              {d.isExtra ? d.theme : displayTheme(d.theme)}
            </span>
            <span className="text-slate-500 text-[10px] tabular-nums flex-shrink-0">₹{d.value.toFixed(1)} Cr</span>
            <span className="font-semibold text-slate-900 tabular-nums flex-shrink-0">{d.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
